import React, { useLayoutEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Pencil, Trash2 } from 'lucide-react'
import { getPlatformAPI } from '@/src/renderer/lib/platform'
import { WithTooltip } from '@/src/renderer/components/ui/tooltip'
import { commentTime, type CommentEntry } from './commentDays'
import { splitCommentText } from './commentText'

export interface CommentItemProps {
  comment: CommentEntry
  /** Schreibt den geänderten, getrimmten Text — nur, wenn er sich wirklich geändert hat. */
  onEdit: (id: string, content: string) => void
  onDelete: (id: string) => void
  /** Beginnt im Ändern — für die Story dieses Zustands. */
  initiallyEditing?: boolean
}

/**
 * **Ein Kommentar** (#183): der Text in einer Blase im Ton des Lebensbereichs, die Uhrzeit unten
 * rechts darin — wie bei WhatsApp. Die Autoren-Pille steht einmal über der ganzen Folge
 * (`TaskComments`). Ändern und Löschen erscheinen beim Überfahren neben der Blase; Ändern ersetzt
 * sie durch ein Feld an derselben Stelle.
 */
export const CommentItem: React.FC<CommentItemProps> = ({ comment, onEdit, onDelete, initiallyEditing = false }) => {
  const { t, i18n } = useTranslation()
  const [editing, setEditing] = useState(initiallyEditing)
  const [draft, setDraft] = useState(comment.content)
  const fieldRef = useRef<HTMLTextAreaElement>(null)

  useLayoutEffect(() => {
    const field = fieldRef.current
    if (!editing || !field) return
    field.style.height = ''
    if (field.scrollHeight > 0) field.style.height = `${field.scrollHeight}px`
  }, [editing, draft])

  const startEditing = () => {
    setDraft(comment.content)
    setEditing(true)
    // Nach dem Bild, in dem das Feld entsteht: Caret ans Ende, nichts markiert.
    requestAnimationFrame(() => {
      const field = fieldRef.current
      if (!field) return
      field.focus()
      field.setSelectionRange(field.value.length, field.value.length)
    })
  }

  // Leer geräumt ändert nichts — gelöscht wird über den Knopf, mit ⌘Z.
  const finishEditing = () => {
    const next = draft.trim()
    setEditing(false)
    if (next && next !== comment.content) onEdit(comment.id, next)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.nativeEvent.isComposing) return
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      finishEditing()
      return
    }
    if (e.key === 'Escape') {
      // Bricht nur das Ändern ab; die Details geben die Tastatur damit noch nicht ab.
      e.preventDefault()
      e.stopPropagation()
      setDraft(comment.content)
      setEditing(false)
    }
  }

  const time = commentTime(comment.postedAt, i18n.language)

  return (
    <article className={`comment-item${editing ? ' comment-item--editing' : ''}`}>
      {editing ? (
        <textarea
          ref={fieldRef}
          className="comment-item__field"
          aria-label={t('taskComments.editField')}
          rows={1}
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={finishEditing}
          spellCheck={false}
        />
      ) : (
        <div className="comment-item__bubble">
          {/*
            `data-time` hält am Ende der letzten Zeile den Platz der Uhrzeit frei (`::after`, unsichtbar):
            Passt sie dort hin, steht sie neben dem Text, sonst bekommt sie eine eigene Zeile.
          */}
          <p className="comment-item__text" data-time={time}>
            {splitCommentText(comment.content).map((piece, i) => (piece.kind === 'link' ? (
              <a
                key={i}
                className="comment-item__link"
                href={piece.href}
                target="_blank"
                rel="noopener noreferrer"
                // Über die Plattform: im Desktop der Systembrowser, im Web ein neuer Tab.
                onClick={e => {
                  e.preventDefault()
                  getPlatformAPI().openExternal(piece.href).catch(err => console.error('[Comments] Failed to open link:', err))
                }}
              >
                {piece.text}
              </a>
            ) : (
              <React.Fragment key={i}>{piece.text}</React.Fragment>
            )))}
          </p>
          <time className="comment-item__time" dateTime={comment.postedAt.toISOString()}>{time}</time>
        </div>
      )}
      {!editing && (
        <span className="comment-item__actions">
          <WithTooltip content={t('taskComments.edit')}>
            <button type="button" className="comment-item__action" aria-label={t('taskComments.edit')} onClick={startEditing}>
              <Pencil size={14} aria-hidden="true" />
            </button>
          </WithTooltip>
          <WithTooltip content={t('taskComments.delete')}>
            <button type="button" className="comment-item__action" aria-label={t('taskComments.delete')} onClick={() => onDelete(comment.id)}>
              <Trash2 size={14} aria-hidden="true" />
            </button>
          </WithTooltip>
        </span>
      )}
    </article>
  )
}
